/**
 * KinsCircle Phase 6D.2 — Legacy Bookmarks (local-only persistence).
 *
 * STABILITY.md §13–§14:
 *   - Bookmarks are stored EXCLUSIVELY in localStorage. NEVER written
 *     to Supabase, NEVER mutate any memory row.
 *   - Storage key: `kinscircle.legacy.bookmarks.v1`.
 *   - Every helper early-returns safely (no throw, no side effect)
 *     when the `legacyBookmarks` flag is OFF, the master `legacyTimeline`
 *     flag is OFF, or `localStorage` is unavailable (SSR, Safari private,
 *     iOS in-app webviews with storage stripped).
 *   - Hard cap: 200 bookmarks per browser. Oldest entries are evicted
 *     LRU-style on overflow so the user never gets a write error.
 *   - Bookmarks reference memory IDs ONLY — no captions, no PII, no
 *     storage URLs are duplicated.
 *
 * Targets:
 *   - 'memory'      → an individual memory the user wants to revisit
 *   - 'collection'  → a curated playlist / collection (by id)
 *   - 'chapter'     → a documentary-style chapter (by id)
 *   - 'playlist'    → an ad-hoc memory playlist (by id)
 */

import { isLegacyBookmarksEnabled } from './featureFlag';

export type LegacyBookmarkKind =
  | 'memory'
  | 'collection'
  | 'chapter'
  | 'playlist';

export type LegacyBookmark = {
  /** Stable composite id: `${kind}:${targetId}`. */
  id: string;
  kind: LegacyBookmarkKind;
  /** ID of the target object — a memory id, collection id, etc. */
  targetId: string;
  /** Optional human-readable label, used so the bookmarks list is scannable. */
  label?: string | null;
  /** Optional short note the user typed (≤120 chars). */
  note?: string | null;
  /** Epoch ms — used for LRU eviction + sort order. */
  createdAt: number;
};

const LS_KEY = 'kinscircle.legacy.bookmarks.v1';
const HARD_CAP = 200;
const NOTE_MAX = 120;

const safeStorage = (): Storage | null => {
  if (typeof window === 'undefined') return null;
  try {
    if (!window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
};

const readAllRaw = (): LegacyBookmark[] => {
  const ls = safeStorage();
  if (!ls) return [];
  try {
    const raw = ls.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Defensive: drop anything that doesn't match the expected shape.
    return parsed.filter(
      (b): b is LegacyBookmark =>
        b &&
        typeof b === 'object' &&
        typeof b.id === 'string' &&
        typeof b.targetId === 'string' &&
        typeof b.createdAt === 'number' &&
        (b.kind === 'memory' ||
          b.kind === 'collection' ||
          b.kind === 'chapter' ||
          b.kind === 'playlist'),
    );
  } catch {
    return [];
  }
};

const writeAllRaw = (rows: LegacyBookmark[]): void => {
  const ls = safeStorage();
  if (!ls) return;
  try {
    ls.setItem(LS_KEY, JSON.stringify(rows));
  } catch {
    /* quota or disabled — non-fatal, the layer is best-effort. */
  }
};

const buildId = (kind: LegacyBookmarkKind, targetId: string): string =>
  `${kind}:${targetId}`;

const trim = (s: string | null | undefined, max: number): string | null => {
  if (!s) return null;
  const t = s.trim();
  if (!t) return null;
  return t.length > max ? t.slice(0, max - 1).trim() + '…' : t;
};

// ── Public API ──────────────────────────────────────────────────────────────

/**
 * Persist a bookmark. Returns the saved row, or null if the feature
 * flag is off / storage is unavailable. Idempotent: re-saving the same
 * `(kind, targetId)` pair refreshes its `createdAt` (LRU touch) and
 * updates the label/note if provided.
 */
export const saveLegacyBookmark = (input: {
  kind: LegacyBookmarkKind;
  targetId: string;
  label?: string | null;
  note?: string | null;
}): LegacyBookmark | null => {
  if (!isLegacyBookmarksEnabled()) return null;
  if (!input || !input.targetId) return null;

  const all = readAllRaw();
  const id = buildId(input.kind, input.targetId);
  const now = Date.now();
  const idx = all.findIndex((b) => b.id === id);

  const row: LegacyBookmark = {
    id,
    kind: input.kind,
    targetId: input.targetId,
    label: trim(input.label ?? (idx >= 0 ? all[idx].label : null), 80),
    note: trim(input.note ?? (idx >= 0 ? all[idx].note : null), NOTE_MAX),
    createdAt: now,
  };

  const next = idx >= 0 ? [...all.slice(0, idx), ...all.slice(idx + 1), row] : [...all, row];

  // LRU eviction: drop oldest entries until we're under the cap.
  next.sort((a, b) => a.createdAt - b.createdAt);
  while (next.length > HARD_CAP) next.shift();

  writeAllRaw(next);
  return row;
};

/** Remove a single bookmark. Returns true iff a row was removed. */
export const removeLegacyBookmark = (
  kind: LegacyBookmarkKind,
  targetId: string,
): boolean => {
  if (!isLegacyBookmarksEnabled()) return false;
  const all = readAllRaw();
  const id = buildId(kind, targetId);
  const next = all.filter((b) => b.id !== id);
  if (next.length === all.length) return false;
  writeAllRaw(next);
  return true;
};

/** Read all bookmarks, newest first. Returns [] when the flag is OFF. */
export const readLegacyBookmarks = (
  filter?: { kind?: LegacyBookmarkKind },
): LegacyBookmark[] => {
  if (!isLegacyBookmarksEnabled()) return [];
  const all = readAllRaw();
  const filtered = filter?.kind ? all.filter((b) => b.kind === filter.kind) : all;
  return [...filtered].sort((a, b) => b.createdAt - a.createdAt);
};

/** Cheap existence check — used to render filled vs. outline icons. */
export const isBookmarked = (
  kind: LegacyBookmarkKind,
  targetId: string,
): boolean => {
  if (!isLegacyBookmarksEnabled()) return false;
  const id = buildId(kind, targetId);
  return readAllRaw().some((b) => b.id === id);
};

/** Wipe every bookmark — used by the admin diagnostics page. */
export const clearLegacyBookmarks = (): void => {
  const ls = safeStorage();
  if (!ls) return;
  try {
    ls.removeItem(LS_KEY);
  } catch {
    /* non-fatal */
  }
};

/** Cheap snapshot stats — for the admin viewer. */
export const readBookmarkStats = (): {
  total: number;
  byKind: Record<LegacyBookmarkKind, number>;
  oldest: number | null;
  newest: number | null;
} => {
  const all = readAllRaw();
  const byKind: Record<LegacyBookmarkKind, number> = {
    memory: 0,
    collection: 0,
    chapter: 0,
    playlist: 0,
  };
  let oldest: number | null = null;
  let newest: number | null = null;
  for (const b of all) {
    byKind[b.kind] = (byKind[b.kind] ?? 0) + 1;
    if (oldest === null || b.createdAt < oldest) oldest = b.createdAt;
    if (newest === null || b.createdAt > newest) newest = b.createdAt;
  }
  return { total: all.length, byKind, oldest, newest };
};
